import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { ConnectDragSource } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDispatch, useSelector } from 'react-redux';
import ActionIcon from 'components/ActionIcon';
import { IconType } from 'components/Icon/constants';
import { ModalType } from 'const';
import { areActionsDisabled as areActionsDisabledSelector } from 'containers/App/selectors';
import { deleteSSI } from 'containers/Artboards/actions';
import { SSISource as SSISourceSelector } from 'containers/Artboards/selectors';
import { showModal } from 'containers/ModalManager/actions';
import { SSISourceWindowOptions } from 'models';
import styles from './styles.module.scss';
import { useSSIActionsDragSource } from './useSSIActionsDragSource';

type Props = {
  connectDragSource: ConnectDragSource;
};

const SSIActions: React.FC<Props> = ({
  connectDragSource,
}) => {
  const dispatch = useDispatch();
  const SSISource = useSelector(SSISourceSelector);
  const areActionsDisabled = useSelector(areActionsDisabledSelector);
  const [showActions, setShowActions] = useState(false);
  const [rightActionsExpanded, setRightActionsExpanded] = useState(false);
  const { dragRef, preview } = useSSIActionsDragSource();
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const show = useCallback(() => {
    if (!areActionsDisabled) {
      setShowActions(true);
    }
  }, [areActionsDisabled]);

  const hide = useCallback(() => {
    setShowActions(false);
    setRightActionsExpanded(false);
  }, []);

  const handleMouseEnterOnRightActionMenu = useCallback(() => {
    if (!areActionsDisabled) {
      setShowActions(true);
      setRightActionsExpanded(true);
    }
  }, [areActionsDisabled]);

  const setSSISource = useCallback(() => {
    dispatch(showModal(ModalType.SSI_SOURCE, SSISource ? SSISource.toJS() as SSISourceWindowOptions : null));
  }, [dispatch, SSISource]);

  const handleDragStart = (event: React.DragEvent): void => {
    const obj = JSON.stringify({});
    event.dataTransfer.setData('text/plain', obj);
  };

  const handleDeleteSSI = (): void => {
    dispatch(deleteSSI());
  };

  return (
    <div
      className={classNames(styles.ssiActions, {
        [styles.showActions]: showActions,
      })}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <div className={styles.left}>
        <div className={styles.leftContainer}>
          <div className={styles.leftActions}>
            {connectDragSource(
              <div
                className={styles.leftActionsIcon}
                onMouseDown={hide}
                onMouseUp={show}
              >
                <ActionIcon type={IconType.MOVE} size="xs" color="secondary" />
              </div>,
            )}
          </div>
          <div className={styles.leftBorder} />
        </div>
      </div>
      <div className={styles.right}>
        <div
          className={classNames(styles.rightContainer, {
            [styles.rightContainer__expanded]: rightActionsExpanded,
          })}
        >
          <div
            className={styles.rightBorder}
            onMouseEnter={show}
            onMouseLeave={hide}
          />
          <div className={styles.rightActionsContainer}>
            <div
              onMouseEnter={handleMouseEnterOnRightActionMenu}
              onMouseLeave={hide}
              className={classNames(styles.rightActions, {
                [styles.rightActions__expanded]: rightActionsExpanded,
              })}
            >
              {rightActionsExpanded ? (
                <div className={styles.rightActionsIcons}>
                  {dragRef(
                    <div onDragStart={handleDragStart}>
                      <ActionIcon
                        className={styles.anchorDragSource}
                        type={IconType.ANCHOR}
                        size="xs"
                        color="secondary"
                      />
                    </div>,
                  )}
                  <ActionIcon
                    type={IconType.LIST}
                    size="xs"
                    color="secondary"
                    onClick={setSSISource}
                  />
                  <ActionIcon
                    type={IconType.DELETE}
                    size="xs"
                    color="secondary"
                    onClick={handleDeleteSSI}
                  />
                </div>
              ) : (
                <div className={styles.rightActionsMenu}>
                  <ActionIcon type={IconType.MENU} size="xs" color="secondary" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSIActions;

